import React, {useEffect, useRef} from 'react';
import {
    ColumnDirective, ColumnModel,
    ColumnsDirective,
    Edit, Filter, FilterSettingsModel, GridComponent,
    Inject, Page, PageSettingsModel,
    PdfExport,
    Reorder,
    Resize, Search,
    Sort, TextWrapSettingsModel
} from "@syncfusion/ej2-react-grids";
import {GridSkeleton} from "../../../../components/Skeletons/GridSkeleton";
import {FramePackingData, Prediction, ThrowPackingData} from "../../../../autogenerate/api.generated.clients";
import {getValue} from "@syncfusion/ej2-base";
import {convertDateStringToLocalTime} from "../../../../utilities/dateHelper";

interface FleetPackingsHealthGridProps {
    data: FramePackingData[] | undefined,
    predictionFilter?: string | undefined, 
    isLoading?: boolean
}

interface FleetPackingGridRowData extends ThrowPackingData {
    frameSerialNumber: string,
    predictionString: string
}

function FleetPackingsHealthGrid({data, isLoading, predictionFilter}: FleetPackingsHealthGridProps) {
    const toolbarOptions = ['Search'];
    const grid = useRef<GridComponent>(null);
    const filterOptions: FilterSettingsModel = {type: 'Excel'};
    const textWrapSettings: TextWrapSettingsModel = {wrapMode: "Header"}
    const pageSettings: PageSettingsModel = { pageSize: 100 };
    
    const gridData = data?.flatMap(framePackingData => {
        return framePackingData.throwPackingData.map((packingData) => {
            let rowData: FleetPackingGridRowData = packingData as FleetPackingGridRowData;
            rowData.frameSerialNumber = framePackingData.frameSerialNumber;
            rowData.predictionString = Prediction[rowData.prediction];
            return rowData;
        });
    });

    useEffect(() => {
        if (predictionFilter && grid.current) {
            grid.current.filterByColumn('predictionString', 'equal', predictionFilter);
        } else {
            grid.current?.clearFiltering();
        }
    }, [predictionFilter]);

    if (isLoading)
    {
        return (
            <div className="e-card" style={{height:'100%'}}>
                <GridSkeleton columns={6} rows={20}/>
            </div>
        );
    }


    const dataBound = () => {
        if (grid) {
            grid.current?.autoFitColumns(['frameSerialNumber', 'mLinkSerial' ,'missingVariableCount', 'extraVariableCount', 'goodVariableCount']);
        }
    };

    const created = () => {
        document?.getElementById(grid.current?.element.id + "_searchbar")?.addEventListener('keyup', (event) => {
            grid.current?.search((event.target as HTMLInputElement).value)
        });
    }
    const timestampAccessor = (field: string, data: object, column: ColumnModel) => {
        const dateString: string = getValue('eventTime', data);
        return convertDateStringToLocalTime(dateString);
    }
    return (
        <div className="e-card" style={{height:'100%', paddingTop: '1em'}}>
            <span className={"e-card-header"} style={{padding: '1em'}}>Fleet Packings</span>

            <GridComponent style={{padding: '1em'}}
                           width="auto" height="100%"
                           ref={grid}
                           enableStickyHeader
                           toolbar={toolbarOptions}
                           created={created}
                           dataSource={gridData}
                           allowResizing
                           allowPaging
                           allowSorting
                           allowFiltering
                           statelessTemplates={['directiveTemplates']}
                           pageSettings={pageSettings}
                           allowTextWrap
                           textWrapSettings={textWrapSettings}
                           filterSettings={filterOptions} dataBound={dataBound} allowReordering
                           loadingIndicator={{indicatorType: 'Shimmer'}}>
                <ColumnsDirective>
                    <ColumnDirective field="frameSerialNumber" width="auto" textAlign="Left" headerText="Frame SN"/>
                    <ColumnDirective field="throwNumber" width="auto" textAlign="Right" headerText="Throw"/>
                    <ColumnDirective field="packingVentTemperature" width="auto" textAlign="Right" headerText="Temp (ºF)"/>
                    <ColumnDirective field="eventTime" width="auto" textAlign="Right" headerText="Timestamp" valueAccessor={timestampAccessor}/>
                    <ColumnDirective field="predictionString" width="auto" textAlign="Left" headerText="Prediction"/>
                    <ColumnDirective field="leakSeverity" width="auto" textAlign="Right" headerText="Leak Severity (%)"/>
                </ColumnsDirective>
                <Inject services={[Resize, Search, Sort, Reorder, PdfExport, Edit, Filter, Page]}/>
            </GridComponent>
        </div>
    );
}

export default FleetPackingsHealthGrid;